(() => {
  const rootSelector = '[data-compatible-mask-root]';
  const overlaySelector = '.compatible-mask-overlay';

  const TYPE_OPTION_SLUG = 'type-de-coques';
  const COLOR_OPTION_SLUG = 'couleur-socool';
  const COLOR_FALLBACK = '—';
  const TYPE_TO_FIELD_KEYS = {
    standard: { mask: 'masque_pour_coque_en_silicone_imprimee', bg: 'fond_pour_coque_en_silicone_imprimee' },
    renforcee: { mask: 'masque_pour_coque_en_silicone_renforcee_imprimee', bg: 'fond_pour_coque_en_silicone_renforcee_imprimee' },
    bumper: { mask: 'masque_pour_coque_bumper', bg: 'fond_pour_coque_bumper' },
    magsafe: { mask: 'masque_pour_coque_magsafe', bg: 'fond_pour_coque_magsafe' },
    'socool argent': { mask: 'masque_pour_coque_socool_argent', bg: 'fond_pour_coque_socool_argent' },
    'socool bleu': { mask: 'masque_pour_coque_socool_bleu', bg: 'fond_pour_coque_socool_bleu' },
    'socool noir': { mask: 'masque_pour_coque_socool_noir', bg: 'fond_pour_coque_socool_noir' },
    'socool rose': { mask: 'masque_pour_coque_socool_rose', bg: 'fond_pour_coque_socool_rose' },
    'socool vert': { mask: 'masque_pour_coque_socool_vert', bg: 'fond_pour_coque_socool_vert' },
    'socool violet': { mask: 'masque_pour_coque_socool_violet', bg: 'fond_pour_coque_socool_violet' },
  };

  class CompatibleMaskManager {
    constructor(root) {
      this.root = root;
      this.scope = root.closest('.featured-product') || document;
      this.productForm = null;
      this.defaultMaskUrl = root.dataset.compatibleMaskDefault || '';
      this.defaultBackgroundUrl = root.dataset.compatibleBackgroundDefault || '';
      this.masksByType = {};
      this.bgsByType = {};
      for (const type of Object.keys(TYPE_TO_FIELD_KEYS)) {
        this.masksByType[type] = {};
        this.bgsByType[type] = {};
      }
      Object.assign(this.masksByType.standard, this.parseModelMasks());
      Object.assign(this.masksByType.renforcee, this.parseReinforcedModelMasks());
      Object.assign(this.bgsByType.standard, this.parseJSONScript('[data-compatible-background-models]'));
      Object.assign(this.bgsByType.renforcee, this.parseJSONScript('[data-compatible-background-models-renforcee]'));
      this.currentMaskUrl = this.defaultMaskUrl;
      this.currentBackgroundUrl = this.defaultBackgroundUrl;
      this.pendingFrame = null;

      this.handleMutations = this.handleMutations.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleOptionSelection = this.handleOptionSelection.bind(this);

      this.observer = new MutationObserver(this.handleMutations);
      this.observer.observe(this.scope, { childList: true, subtree: true });

      this.bindProductForm();
      this.subscribeToOptionSelection();
      this.currentMaskUrl = this.resolveMaskUrl();
      this.currentBackgroundUrl = this.resolveBackgroundUrl();

      this.applyMask();
      this.applyBackground();

      // Tant que les modeles au-dela des 50 premiers (plafond Liquid) ne sont pas charges, le
      // masque courant peut encore etre celui par defaut : phone-preview.js suit les
      // `data-current-*-url` et se met a jour a la resolution.
      this.fetchAllModelsAsync();
    }

    async fetchAllModelsAsync() {
      const token = this.root.dataset.storefrontToken;
      const handle = this.root.dataset.productHandle;
      const namespace = this.root.dataset.metafieldNamespace || 'custom';
      const key = this.root.dataset.metafieldKey || 'modeles_compatibles';
      if (!token || !handle) return;

      const cacheKey = `cm-models-${handle}`;
      const ttlMs = 60 * 60 * 1000;
      try {
        const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
        if (cached && Date.now() - cached.ts < ttlMs && Array.isArray(cached.data)) {
          this.mergeMetaobjectsIntoMaps(cached.data);
          this.reapplyAfterMerge();
          return;
        }
      } catch (_) { /* ignore */ }

      let metaobjects;
      try {
        metaobjects = await this.paginateModelsViaStorefrontAPI(handle, token, namespace, key);
      } catch (error) {
        console.warn('[compatible-mask] storefront API fetch failed:', error);
        return;
      }
      if (!metaobjects.length) return;

      try {
        localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data: metaobjects }));
      } catch (_) { /* quota — ignore */ }

      this.mergeMetaobjectsIntoMaps(metaobjects);
      this.reapplyAfterMerge();
    }

    async paginateModelsViaStorefrontAPI(handle, token, namespace, key) {
      const endpoint = '/api/2024-10/graphql.json';
      const query = `
        query GetCompatibleModels($handle: String!, $namespace: String!, $key: String!, $cursor: String) {
          product(handle: $handle) {
            metafield(namespace: $namespace, key: $key) {
              references(first: 250, after: $cursor) {
                edges {
                  cursor
                  node {
                    ... on Metaobject {
                      fields {
                        key
                        value
                        reference {
                          ... on MediaImage {
                            image { url }
                          }
                        }
                      }
                    }
                  }
                }
                pageInfo { hasNextPage endCursor }
              }
            }
          }
        }
      `;

      const all = [];
      let cursor = null;
      let safety = 0;
      while (safety++ < 20) {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token,
          },
          body: JSON.stringify({ query, variables: { handle, namespace, key, cursor } }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        if (payload.errors) throw new Error(`GraphQL errors: ${JSON.stringify(payload.errors)}`);
        const refs = payload?.data?.product?.metafield?.references;
        if (!refs) break;

        for (const edge of refs.edges) {
          if (edge?.node) all.push(edge.node);
        }
        if (!refs.pageInfo?.hasNextPage) break;
        cursor = refs.pageInfo.endCursor;
      }
      return all;
    }

    mergeMetaobjectsIntoMaps(metaobjects) {
      for (const obj of metaobjects) {
        if (!obj?.fields) continue;
        const byKey = {};
        for (const f of obj.fields) byKey[f.key] = f;

        const nom = byKey.nom?.value?.toString().toLowerCase();
        if (!nom) continue;

        for (const [type, keys] of Object.entries(TYPE_TO_FIELD_KEYS)) {
          const maskUrl = byKey[keys.mask]?.reference?.image?.url;
          const bgUrl = byKey[keys.bg]?.reference?.image?.url;
          if (maskUrl) this.masksByType[type][nom] = maskUrl;
          if (bgUrl) this.bgsByType[type][nom] = bgUrl;
        }
      }
    }

    readOptionValue(slug) {
      const wrapper = this.scope.querySelector?.(`[data-option-slug="${slug}"]`)
        || document.querySelector(`[data-option-slug="${slug}"]`);
      if (!wrapper) return '';
      const select = wrapper.querySelector('select');
      if (select) return select.value;
      // Option rendue en vignettes (boutons radio)
      return wrapper.querySelector('input[type="radio"]:checked')?.value || '';
    }

    detectCoqueType() {
      let type = this.normalizeValue(this.readOptionValue(TYPE_OPTION_SLUG) || 'standard') || 'standard';

      if (type === 'socool') {
        const color = this.normalizeValue(this.readOptionValue(COLOR_OPTION_SLUG));
        if (color && color !== this.normalizeValue(COLOR_FALLBACK) && color !== '-') {
          type = `socool ${color}`;
        }
      }
      return TYPE_TO_FIELD_KEYS[type] ? type : 'standard';
    }

    reapplyAfterMerge() {
      const labels = this.readSelectedOptionLabels();
      const pseudoVariant = labels.length
        ? {
            title: labels.join(' / '),
            option1: labels[0] || '',
            option2: labels[1] || '',
            option3: labels[2] || '',
          }
        : null;

      this.currentMaskUrl = this.resolveMaskUrl(pseudoVariant);
      this.currentBackgroundUrl = this.resolveBackgroundUrl(pseudoVariant);
      this.applyMask();
      this.applyBackground();
    }

    subscribeToOptionSelection() {
      const pubsub = window.theme?.pubsub;
      const eventName = pubsub?.PUB_SUB_EVENTS?.optionValueSelectionChange;
      if (!pubsub || !eventName) return;

      pubsub.subscribe(eventName, this.handleOptionSelection);
    }

    readSelectedOptionLabels() {
      const variantPicker = this.scope.querySelector('variant-picker');
      if (!variantPicker) return [];

      return Array.from(variantPicker.querySelectorAll('select, fieldset'))
        .map((el) => {
          if (el.tagName === 'SELECT') return el.value;
          return el.querySelector('input:checked')?.value || '';
        })
        .filter(Boolean);
    }

    handleOptionSelection({ data: { event } }) {
      if (!event || !this.scope.contains(event.target)) return;

      const labels = this.readSelectedOptionLabels();
      if (!labels.length) return;

      const pseudoVariant = {
        title: labels.join(' / '),
        option1: labels[0] || '',
        option2: labels[1] || '',
        option3: labels[2] || '',
      };

      this.currentMaskUrl = this.resolveMaskUrl(pseudoVariant);
      this.currentBackgroundUrl = this.resolveBackgroundUrl(pseudoVariant);
      this.applyMask();
      this.applyBackground();
    }

    bindProductForm() {
      const nextProductForm = document.getElementById(this.root.dataset.productFormId) || document.forms[this.root.dataset.productFormId] || null;

      if (this.productForm === nextProductForm) {
        return;
      }

      if (this.productForm) {
        this.productForm.removeEventListener('variant:change', this.handleVariantChange);
      }

      this.productForm = nextProductForm;

      if (this.productForm) {
        this.productForm.addEventListener('variant:change', this.handleVariantChange);
      }
    }

    parseModelMasks() {
      return this.parseJSONScript('[data-compatible-mask-models]');
    }

    parseReinforcedModelMasks() {
      return this.parseJSONScript('[data-compatible-mask-models-renforcee]');
    }

    parseJSONScript(selector) {
      const payload = this.root.querySelector(selector);

      if (!payload) {
        return {};
      }

      try {
        return JSON.parse(payload.textContent || '{}');
      } catch (error) {
        return {};
      }
    }

    normalizeValue(value) {
      return (value || '')
        .toString()
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/\s+/g, ' ');
    }

    isReinforcedVariant(variant) {
      if (!variant) {
        return false;
      }

      return [variant.title, variant.option1, variant.option2, variant.option3]
        .map((value) => this.normalizeValue(value))
        .some((value) => value.includes('renforcee'));
    }

    getCurrentVariantData() {
      const selectedVariantNode = this.scope.querySelector('[data-selected-variant]');

      if (!selectedVariantNode) {
        return null;
      }

      try {
        return JSON.parse(selectedVariantNode.textContent || 'null');
      } catch (error) {
        return null;
      }
    }

    getMaskFromVariantData(variant) {
      const type = this.detectCoqueType();
      return this.matchByVariantData(variant, this.masksByType[type] || this.masksByType.standard);
    }

    getBackgroundFromVariantData(variant) {
      const type = this.detectCoqueType();
      return this.matchByVariantData(variant, this.bgsByType[type] || this.bgsByType.standard);
    }

    matchByVariantData(variant, collection) {
      if (!variant || !collection || !Object.keys(collection).length) {
        return '';
      }

      const candidates = [variant.title, variant.option1, variant.option2, variant.option3]
        .map((value) => this.normalizeValue(value))
        .filter(Boolean);

      const modelNames = Object.keys(collection).sort((a, b) => b.length - a.length);

      // Pass 1: exact match
      for (const candidate of candidates) {
        if (modelNames.includes(candidate)) {
          return collection[candidate];
        }
      }

      // Pass 2: candidate contains modelName — most specific (longest) model wins
      for (const modelName of modelNames) {
        if (candidates.some((c) => c.includes(modelName))) {
          return collection[modelName];
        }
      }

      // Pass 3: modelName contains candidate (only candidates >= 4 chars to avoid noise)
      for (const candidate of candidates) {
        if (candidate.length < 4) continue;
        const found = modelNames.find((modelName) => modelName.includes(candidate));
        if (found) return collection[found];
      }

      return '';
    }

    resolveMaskUrl(variantData) {
      const resolvedVariantData = variantData || this.getCurrentVariantData();
      return this.getMaskFromVariantData(resolvedVariantData) || this.defaultMaskUrl;
    }

    resolveBackgroundUrl(variantData) {
      const resolvedVariantData = variantData || this.getCurrentVariantData();
      return this.getBackgroundFromVariantData(resolvedVariantData) || this.defaultBackgroundUrl;
    }

    handleMutations() {
      this.bindProductForm();
      this.applyMask();
      this.applyBackground();
    }

    handleVariantChange(event) {
      const variantData = event.detail?.variant;
      this.currentMaskUrl = this.resolveMaskUrl(variantData);
      this.currentBackgroundUrl = this.resolveBackgroundUrl(variantData);
      this.applyMask();
      this.applyBackground();

      if (this.pendingFrame) {
        window.cancelAnimationFrame(this.pendingFrame);
      }

      this.pendingFrame = window.requestAnimationFrame(() => {
        this.pendingFrame = window.requestAnimationFrame(() => {
          this.applyMask();
          this.applyBackground();
          this.pendingFrame = null;
        });
      });
    }

    syncImageSrc(img, url) {
      if (!img) return;
      if (img.getAttribute('src') !== url) {
        img.setAttribute('src', url);
      }
      if (img.hasAttribute('srcset')) {
        img.removeAttribute('srcset');
      }
      if (img.hasAttribute('sizes')) {
        img.removeAttribute('sizes');
      }
    }

    applyMask() {
      const maskUrl = this.currentMaskUrl || this.defaultMaskUrl;
      // Source de verite pour phone-preview.js : les <img> du DOM peuvent etre masquees
      // (galerie `hidden xl:block`), en lazy jamais charge, ou garder un `currentSrc` perime.
      this.root.dataset.currentMaskUrl = maskUrl || '';
      const printAreas = this.scope.querySelectorAll('.tee-printarea.tee-printarea--image');

      printAreas.forEach((printArea) => {
        let overlay = printArea.querySelector(`:scope > ${overlaySelector}`);

        if (!maskUrl) {
          overlay?.remove();
          return;
        }

        if (!overlay) {
          overlay = document.createElement('div');
          overlay.className = 'compatible-mask-overlay';
          overlay.setAttribute('aria-hidden', 'true');
          overlay.style.position = 'absolute';
          overlay.style.inset = '0';
          overlay.style.zIndex = '2';
          overlay.style.pointerEvents = 'none';

          const image = document.createElement('img');
          image.alt = '';
          image.loading = 'lazy';
          image.style.width = '100%';
          image.style.height = '100%';
          image.style.objectFit = 'contain';
          image.style.display = 'block';

          overlay.appendChild(image);
          printArea.appendChild(overlay);
        }

        if (getComputedStyle(printArea).position === 'static') {
          printArea.style.position = 'relative';
        }

        if (getComputedStyle(printArea).zIndex === 'auto') {
          printArea.style.zIndex = '1';
        }

        this.syncImageSrc(overlay.querySelector('img'), maskUrl);
      });

      this.scope.querySelectorAll('.product__compatible-mask img').forEach((img) => {
        this.syncImageSrc(img, maskUrl);
      });
    }

    applyBackground() {
      const backgroundUrl = this.currentBackgroundUrl || this.defaultBackgroundUrl;
      this.root.dataset.currentBackgroundUrl = backgroundUrl || '';
      const printAreas = this.scope.querySelectorAll('.tee-printarea.tee-printarea--image');

      printAreas.forEach((printArea) => {
        if (!backgroundUrl) {
          if (printArea.dataset.compatibleBackgroundApplied === 'true') {
            printArea.style.backgroundImage = '';
            printArea.style.backgroundSize = '';
            printArea.style.backgroundRepeat = '';
            printArea.style.backgroundPosition = '';
            delete printArea.dataset.compatibleBackgroundApplied;
          }
          return;
        }

        const desiredImage = `url("${backgroundUrl}")`;
        if (printArea.style.backgroundImage !== desiredImage) {
          printArea.style.backgroundImage = desiredImage;
        }
        printArea.style.backgroundSize = 'contain';
        printArea.style.backgroundRepeat = 'no-repeat';
        printArea.style.backgroundPosition = 'center';
        printArea.dataset.compatibleBackgroundApplied = 'true';
      });

      this.scope.querySelectorAll('.product__compatible-background-img').forEach((img) => {
        this.syncImageSrc(img, backgroundUrl);
      });
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll(rootSelector).forEach((root) => {
      if (root.dataset.compatibleMaskReady === 'true') {
        return;
      }

      root.dataset.compatibleMaskReady = 'true';
      new CompatibleMaskManager(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();